import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useTrainerMedia } from '@/hooks/useTrainerMedia';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SessionEditor from '@/components/trainers/SessionEditor';
import { useToast } from '@/components/ui/use-toast';
import {
  Loader2,
  X,
  Search,
  Save,
  Edit2,
  Dumbbell,
  Languages,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import TrainerMediaGallery from '@/components/trainers/TrainerMediaGallery';
import { StripeConnectCard } from '@/components/settings/sections/StripeConnectCard';

const AVAILABLE_LANGUAGES = [
  /* … list unchanged … */
 // A–C
  "Afrikaans","Albanian","Amharic","Arabic","Armenian","Assamese","Aymara","Azerbaijani",
  "Basque","Belarusian","Bengali","Bosnian","Bulgarian","Burmese","Catalan","Cebuano",
  "Chinese","Corsican","Croatian","Czech",
  // D–H
  "Danish","Dhivehi","Dogri","Dutch","Dzongkha",
  "English","Esperanto","Estonian","Ewe",
  "Faroese","Fijian","Finnish","French","Frisian",
  "Galician","Georgian","German","Greek","Guarani","Gujarati",
  "Haitian Creole","Hausa","Hawaiian","Hebrew","Hindi","Hmong","Hungarian",
  // I–L
  "Icelandic","Igbo","Ilocano","Indonesian","Irish","Italian","Japanese","Javanese",
  "Kannada","Kazakh","Khmer","Kinyarwanda","Korean","Kurdish","Kyrgyz",
  "Lao","Latin","Latvian","Lingala","Lithuanian","Luxembourgish",
  // M–P
  "Macedonian","Maithili","Malagasy","Malay","Malayalam","Maltese","Maori","Marathi",
  "Meitei (Manipuri)","Mongolian","Nepali","Norwegian","Nyanja (Chichewa)",
  "Odia (Oriya)","Oromo","Pashto","Persian","Polish","Portuguese","Punjabi",
  // Q–T
  "Quechua","Romanian","Russian",
  "Samoan","Sanskrit","Scots Gaelic","Serbian","Shona","Sindhi","Sinhala",
  "Slovak","Slovenian","Somali","Spanish","Sundanese","Swahili","Swedish",
  "Tagalog","Tajik","Tamil","Tatar","Telugu","Thai","Tigrinya","Tswana","Turkish","Turkmen",
  // U–Z
  "Ukrainian","Urdu","Uyghur","Uzbek", "Valencian",
  "Vietnamese","Welsh","Western Frisian","Xhosa","Yiddish","Yoruba","Zulu",
  // Sign & Classical (optional extras you already had)
  "Sign Language"
];

export default function TrainerSettingsTab() {
const { user, profile } = useCurrentUser();
  const { toast } = useToast();
const { upload } = useTrainerMedia(user?.id);

  /* ──────────────────────────────────────────────────────────────────
     LOCAL STATE
  ────────────────────────────────────────────────────────────────── */
  const [form, setForm] = useState({
    headline: '',
    bio: '',
    hourly_rate: '',
    languages: [] as string[],
  });
  const [initialForm, setInitialForm] = useState(form); // snapshot of the DB state
  const [sessions, setSessions] = useState<any[]>([]);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /* ──────────────────────────────────────────────────────────────────
     FETCH DATA ON LOAD
  ────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        /* trainer_profiles -------------------------------------------------- */
        const { data: prof, error: profErr } = await supabase
          .from('trainer_profiles')
          .select('headline,bio,hourly_rate,languages')
          .eq('user_id', profile.id)
          .single();

        if (profErr && profErr.code !== 'PGRST116') throw profErr; // "row not found" is OK

        /* trainer_session_types -------------------------------------------- */
        const { data: sess, error: sessErr } = await supabase
          .from('trainer_session_types')
          .select('*')
          .eq('user_id', profile.id)
          .order('title');

        if (sessErr) throw sessErr;

        const init = {
          headline: prof?.headline || '',
          bio: prof?.bio || '',
          hourly_rate: prof?.hourly_rate?.toString() || '',
          languages: prof?.languages || [],
        };

        setForm(init);
        setInitialForm(init);
        setSessions(sess || []);
      } catch (e) {
        console.error('Error fetching trainer data:', e);
        toast({
          title: 'Error',
          description: 'Failed to load trainer profile data.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id]);

  /* ──────────────────────────────────────────────────────────────────
     DERIVED STATE
  ────────────────────────────────────────────────────────────────── */
  const filteredLanguages = useMemo(
    () =>
      AVAILABLE_LANGUAGES.filter((l) =>
        l.toLowerCase().includes(languageSearchTerm.toLowerCase())
      ),
    [languageSearchTerm]
  );

  const hasChanges = useMemo(() => {
    return (
      form.headline !== initialForm.headline ||
      form.bio !== initialForm.bio ||
      form.hourly_rate !== initialForm.hourly_rate ||
      JSON.stringify([...form.languages].sort()) !==
        JSON.stringify([...initialForm.languages].sort())
    );
  }, [form, initialForm]);

  /* ──────────────────────────────────────────────────────────────────
     SAVE HELPER
  ────────────────────────────────────────────────────────────────── */
  const saveProfile = async () => {
    if (!profile?.id || !hasChanges) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('trainer_profiles')
        .upsert(
          {
            user_id: profile.id,
            headline: form.headline,
            bio: form.bio,
            hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
            languages: form.languages,
          },
          { onConflict: 'user_id', ignoreDuplicates: false }
        )
        .select();

      if (error) throw error;

      setInitialForm(form); // reset dirty-state
      toast({
        title: 'Success',
        description: 'Trainer profile updated.',
      });
    } catch (e) {
      console.error('Error saving trainer profile:', e);
      toast({
        title: 'Error',
        description: 'Could not save trainer profile.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  /* ──────────────────────────────────────────────────────────────────
     AUTO-SAVE WHEN USER CLOSES AN EDITING SECTION
  ────────────────────────────────────────────────────────────────── */
  const prevSectionRef = useRef<string | null>(null);
  useEffect(() => {
    // leaving a section ⇒ prevSection != null && editingSection == null
    if (prevSectionRef.current && !editingSection && hasChanges) {
      saveProfile(); // fire & forget; UX feels instant
    }
    prevSectionRef.current = editingSection;
  }, [editingSection, hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ──────────────────────────────────────────────────────────────────
     UI HELPERS
  ────────────────────────────────────────────────────────────────── */
  const toggleSection = (section: string) =>
    setEditingSection(section === editingSection ? null : section);

  const cancelEdit = () => {
    // restore DB snapshot, close section
    setForm(initialForm);
    setEditingSection(null);
  };

  const handleLanguageToggle = (lang: string) =>
    setForm((p) => ({
      ...p,
      languages: p.languages.includes(lang)
        ? p.languages.filter((l) => l !== lang)
        : [...p.languages, lang],
    }));

  /* ──────────────────────────────────────────────────────────────────
     LOADING
  ────────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-semibold text-white">Trainer Profile</h2>

      {/* ─── MEDIA GALLERY ─────────────────────────────────────────── */}
      <TrainerMediaGallery
  trainerAuthUid={user?.id}
  isEditable
/>

      {/* ─── ABOUT / BIO ──────────────────────────────────────────── */}
      <section className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
        <header className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Briefcase className="h-5 w-5 text-gold-400 mr-2" />
            About
          </h2>
          <Button
            onClick={() => toggleSection('bio')}
            variant="ghost"
            size="sm"
            className="text-dark-800 hover:text-white"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        </header>

        {editingSection === 'bio' ? (
          <div className="space-y-4">
            {/* Headline ------------------------------------------------------ */}
            <label className="block text-dark-800 mb-2" htmlFor="headline">
              Headline
            </label>
            <Input
              id="headline"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="Your professional headline"
              className="bg-dark-300 border-dark-400 text-white"
            />

            {/* Bio ----------------------------------------------------------- */}
            <label className="block text-dark-800 mb-2" htmlFor="bio">
              Bio
            </label>
            <Textarea
              id="bio"
              rows={6}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Tell us about yourself and your philosophy"
              className="bg-dark-300 border-dark-400 text-white"
            />

            {/* Actions ------------------------------------------------------- */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={cancelEdit}
                variant="outline"
                className="border-dark-400"
              >
                Cancel
              </Button>
              <Button
                disabled={isSaving || !hasChanges}
                onClick={async () => {
                  await saveProfile();
                  setEditingSection(null);
                }}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {form.headline && (
              <h3 className="text-lg font-medium text-white mb-4">
                {form.headline}
              </h3>
            )}
            <p className="text-dark-800 whitespace-pre-line">
              {(() => {
                const full = form.bio.trim() || 'No bio provided yet.';
                if (full.length <= 300) return full;
                return bioExpanded ? full : `${full.slice(0, 300)}…`;
              })()}
            </p>
            {form.bio.trim().length > 300 && (
              <button
                onClick={() => setBioExpanded((p) => !p)}
                className="mt-2 text-sm font-medium text-gold-400 hover:underline"
              >
                {bioExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}
      </section>

     {/* ─── LANGUAGES ───────────────────────────────────────────── */}
<section className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
  <header className="flex items-center justify-between mb-4">
    <h2 className="text-xl font-semibold text-white flex items-center">
      <Languages className="h-5 w-5 text-gold-400 mr-2" />
      Languages
    </h2>
    <Button
      onClick={() => toggleSection('languages')}
      variant="ghost"
      size="sm"
      className="text-dark-800 hover:text-white"
    >
      <Edit2 className="h-4 w-4" />
    </Button>
  </header>

  {editingSection === 'languages' ? (
    <div className="space-y-4">
      {/* Search + chips */}
      <label className="block text-dark-800 mb-2" htmlFor="lang-search">
        Languages
      </label>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-800" />
        <Input
          id="lang-search"
          value={languageSearchTerm}
          onChange={(e) => setLanguageSearchTerm(e.target.value)}
          placeholder="Search languages…"
          className="pl-10 bg-dark-300 border-dark-400 text-white"
        />
      </div>

      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 rounded-md bg-dark-300 border border-dark-400">
        {filteredLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => handleLanguageToggle(lang)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm transition-colors',
              form.languages.includes(lang)
                ? 'bg-gold-400 text-dark-50'
                : 'bg-dark-400 text-white hover:bg-dark-500'
            )}
          >
            {lang}
            {form.languages.includes(lang) && (
              <X className="h-3.5 w-3.5 ml-1.5 inline-block" />
            )}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          onClick={cancelEdit}
          variant="outline"
          className="border-dark-400"
        >
          Cancel
        </Button>
        <Button
          disabled={isSaving || !hasChanges}
          onClick={async () => {
            await saveProfile();
            setEditingSection(null);
          }}
          className="bg-gold-400 hover:bg-gold-500 text-dark-50"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-wrap gap-2">
      {form.languages.length ? (
        form.languages.map((l) => (
          <span
            key={l}
            className="px-3 py-1.5 bg-dark-300 rounded-full text-sm text-white"
          >
            {l}
          </span>
        ))
      ) : (
        <span className="text-dark-800">No languages specified</span>
      )}
    </div>
  )}
</section>

      {/* Stripe Connect Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Payment Setup</h3>
        <StripeConnectCard />
      </div>

      {/* ─── SESSION TYPES ─────────────────────────────────────────── */}
      <section className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
        <header className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Dumbbell className="h-5 w-5 text-gold-400 mr-2" />
            Available Session Types
          </h2>
          <Button
            onClick={() => toggleSection('sessions')}
            variant="ghost"
            size="sm"
            className="text-dark-800 hover:text-white"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        </header>

        {editingSection === 'sessions' ? (
          <>
            <SessionEditor
              sessions={sessions}
              onChange={setSessions}
              userId={profile?.id}
            />
            <div className="flex justify-end mt-4">
              <Button
                onClick={() => setEditingSection(null)}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                Done
              </Button>
            </div>
          </>
        ) : sessions.length === 0 ? (
          <div className="text-center py-6 bg-dark-300 rounded-xl">
            <p className="text-dark-800">No session types added yet.</p>
            <Button
              onClick={() => toggleSection('sessions')}
              className="mt-4 bg-dark-400 hover:bg-dark-500 text-white"
            >
              Add Your First Session
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((s) => {
              const price = (s.price_cents / 100).toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
              return (
                <div
                  key={s.id}
                  className="bg-dark-300 rounded-xl p-4 border border-dark-400 hover:border-gold-400/50 transition-all duration-300"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-base font-medium text-white">
                        {s.title}
                      </h3>
                      {s.description && (
                        <p className="text-dark-800 text-sm mt-1">
                          {s.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-gold-400 font-bold">
                        {price}
                        {s.price_mode === 'per_person' && '/person'}
                      </p>
                      <p className="text-dark-800 text-sm">{s.duration_min} min</p>
                      {s.group_min && s.group_max && (
                        <p className="text-dark-800 text-sm">
                          {s.group_min}-{s.group_max} people
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}